import { useCallback, useMemo, useState } from 'react';
import { usePaginatedList } from '../../../hooks';
import {
  AssignedDivision,
  dispatch,
  Divisions,
  getWorkspaceId,
  SelectedCfa,
  updateCfaDivisions,
  useAppSelector,
  useLazyGetDivisionsQuery,
} from '@zonofi/zono-money-store';

interface UseAddDivisionsProps {
  onClose: () => void;
  currentSelectedCfa?: SelectedCfa;
}

export const useAddDivisions = ({
  onClose,
  currentSelectedCfa,
}: UseAddDivisionsProps) => {
  const sellerWorkspaceId = useAppSelector(getWorkspaceId);

  const [selectedDivisionIds, setSelectedDivisionIds] = useState<
    AssignedDivision[]
  >([]);

  const [fetchDivisionsAPI, fetchDivisionsAPIResponse] =
    useLazyGetDivisionsQuery();

  const fetchDivisionListApiCall = useCallback(
    (params: { pageNo: number; pageSize: number }) => {
      fetchDivisionsAPI({ workspaceId: sellerWorkspaceId, ...params });
    },
    []
  );

  const memoizedDivisionsData = useMemo(() => {
    return fetchDivisionsAPIResponse.data?.divisions ?? [];
  }, [fetchDivisionsAPIResponse.data?.divisions]);

  const {
    data: divisionsData,
    lastItemRef,
    onScroll,
    isEndOfList,
  } = usePaginatedList<Divisions>({
    fetchDataApiCall: fetchDivisionListApiCall,
    apiResponseData: memoizedDivisionsData,
    isLoading: fetchDivisionsAPIResponse.isLoading,
    uniqueKey: 'id',
    dependencies: [],
    isError: fetchDivisionsAPIResponse.isError,
    paginationMeta: {
      startRecord: fetchDivisionsAPIResponse?.data?.startRecord || 0,
      endRecord: fetchDivisionsAPIResponse?.data?.endRecord || 0,
      totalCount: fetchDivisionsAPIResponse?.data?.totalRecords || 0,
    },
  });

  const onDivisionPress = (division: Divisions) => {
    setSelectedDivisionIds((prev) => {
      const exists = prev.find((d) => d.id === division.id);

      if (exists) {
        return prev.filter((d) => d.id !== division.id);
      }

      return [
        ...prev,
        {
          id: division.id,
          name: division.name,
          code: division.code,
        },
      ];
    });
  };

  const onSelectAllPress = () => {
    setSelectedDivisionIds(
      divisionsData.map((division) => ({
        id: division.id,
        name: division.name,
        code: division.code,
      }))
    );
  };

  const onAssignDivisionsPress = () => {
    if (currentSelectedCfa) {
      dispatch(
        updateCfaDivisions({
          cfaId: currentSelectedCfa.id,
          divisions: selectedDivisionIds,
        })
      );
      setSelectedDivisionIds([]);
      onClose();
    }
  };

  return {
    onSelectAllPress,
    onAssignDivisionsPress,
    onDivisionPress,
    divisionsData,
    lastItemRef,
    onScroll,
    isEndOfList,
    setSelectedDivisionIds,
    selectedDivisionIds,
  };
};
